import { createContext, useContext, useMemo, useState } from 'react';
import { useApp } from '../../../context/AppContext';
import {
  parsePartnerDetails,
  calculatePortfolioLinkages,
  parseContributingCenters,
  parseFunders,
  buildCrossProgramMatrix,
  buildPartnerProgramEdges,
  parseCountryList,
} from '../../../utils/dataProcessor';
import { getISO3, isGlobalSouthCountry } from '../../../utils/countryMapping';

/**
 * PartnershipDataContext
 * Shared state for all Partnership (Section 3) charts and components.
 * Own section-local filters (delivery type, W3 project) and all computed data.
 */
const PartnershipDataContext = createContext(null);

/**
 * PartnershipDataProvider
 * Wrap your partnership section content in this provider to share data across all charts.
 */
export function PartnershipDataProvider({ children }) {
  const { stats, filteredResults, reportType, filters } = useApp();

  const isAccelerator = reportType === 'accelerator';

  // Section-local filters
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState('all');
  const [w3ProjectFilter, setW3ProjectFilter] = useState('all');

  // W3 project options
  const w3ProjectOptions = useMemo(() =>
    [...new Set(filteredResults.map(r => r.w3ProjectTitle).filter(Boolean))].sort(),
    [filteredResults]
  );

  // Apply local W3 project filter
  const localFilteredResults = useMemo(() => {
    if (w3ProjectFilter === 'all') return filteredResults;
    return filteredResults.filter(r => r.w3ProjectTitle === w3ProjectFilter);
  }, [filteredResults, w3ProjectFilter]);

  // Results with partners
  const resultsWithPartners = useMemo(
    () => filteredResults.filter(r => r.partners || r.partnersKP),
    [filteredResults]
  );

  // Full partner map (before delivery type filter)
  const fullPartnerMap = useMemo(() => {
    if (!localFilteredResults || localFilteredResults.length === 0) return new Map();
    return parsePartnerDetails(localFilteredResults);
  }, [localFilteredResults]);

  // Filtered partner map (after delivery type filter)
  const partnerMap = useMemo(() => {
    if (deliveryTypeFilter === 'all') return fullPartnerMap;
    const filtered = new Map();
    fullPartnerMap.forEach((p, key) => {
      if (p.deliveryTypes && p.deliveryTypes.includes(deliveryTypeFilter)) {
        filtered.set(key, p);
      }
    });
    return filtered;
  }, [fullPartnerMap, deliveryTypeFilter]);

  // Portfolio linkages (accelerator mode)
  const linkageData = useMemo(
    () => calculatePortfolioLinkages(filteredResults || [], filters.program),
    [filteredResults, filters.program]
  );

  // Phase 2 computed data
  const centerMap = useMemo(() => parseContributingCenters(localFilteredResults || []), [localFilteredResults]);
  const funderMap = useMemo(() => parseFunders(localFilteredResults || []), [localFilteredResults]);
  const crossProgramData = useMemo(() => buildCrossProgramMatrix(localFilteredResults || []), [localFilteredResults]);
  const networkData = useMemo(() => buildPartnerProgramEdges(localFilteredResults || [], filters.program), [localFilteredResults, filters.program]);

  // Funding-split subsets of localFilteredResults
  const w1w2LocalResults = useMemo(
    () => localFilteredResults.filter(r => r.fundingType !== 'W3Bilateral'),
    [localFilteredResults]
  );
  const w3LocalResults = useMemo(
    () => localFilteredResults.filter(r => r.fundingType === 'W3Bilateral'),
    [localFilteredResults]
  );

  // Funding-split partner map: merge w1w2 + w3 partner counts
  const partnerMapByFunding = useMemo(() => {
    const w1Map = parsePartnerDetails(w1w2LocalResults);
    const w3Map = parsePartnerDetails(w3LocalResults);
    const merged = new Map();
    const allKeys = new Set([...w1Map.keys(), ...w3Map.keys()]);
    allKeys.forEach(key => {
      const w1 = w1Map.get(key);
      const w3 = w3Map.get(key);
      merged.set(key, {
        acronym: (w1 || w3).acronym,
        fullName: (w1 || w3).fullName,
        deliveryTypes: (w1 || w3).deliveryTypes,
        resultCount: (w1?.resultCount || 0) + (w3?.resultCount || 0),
        w1w2Count: w1?.resultCount || 0,
        w3Count: w3?.resultCount || 0,
      });
    });
    return merged;
  }, [w1w2LocalResults, w3LocalResults]);

  // Funding-split center map
  const centerMapByFunding = useMemo(() => {
    const w1Map = parseContributingCenters(w1w2LocalResults);
    const w3Map = parseContributingCenters(w3LocalResults);
    const merged = new Map();
    const allKeys = new Set([...w1Map.keys(), ...w3Map.keys()]);
    allKeys.forEach(key => {
      const w1 = w1Map.get(key);
      const w3 = w3Map.get(key);
      merged.set(key, {
        code: (w1 || w3).code,
        fullName: (w1 || w3).fullName,
        isPrimary: (w1?.isPrimary || false) || (w3?.isPrimary || false),
        resultCount: (w1?.resultCount || 0) + (w3?.resultCount || 0),
        w1w2Count: w1?.resultCount || 0,
        w3Count: w3?.resultCount || 0,
      });
    });
    return merged;
  }, [w1w2LocalResults, w3LocalResults]);

  // Funding-split cross-program data
  const crossProgramDataByFunding = useMemo(() => {
    const w1Data = buildCrossProgramMatrix(w1w2LocalResults);
    const w3Data = buildCrossProgramMatrix(w3LocalResults);
    const pairMap = new Map();
    // Merge w1w2 pairs
    w1Data.pairs.forEach(p => {
      const key = `${p.source}|${p.target}`;
      pairMap.set(key, { ...p, w1w2Count: p.count, w3Count: 0 });
    });
    // Merge w3 pairs
    w3Data.pairs.forEach(p => {
      const key = `${p.source}|${p.target}`;
      if (pairMap.has(key)) {
        pairMap.get(key).w3Count = p.count;
        pairMap.get(key).count += p.count;
      } else {
        pairMap.set(key, { ...p, w1w2Count: 0, w3Count: p.count });
      }
    });
    const pairs = [...pairMap.values()].sort((a, b) => b.count - a.count);
    return { pairs, programs: crossProgramData.programs };
  }, [w1w2LocalResults, w3LocalResults, crossProgramData.programs]);

  // Conditional flags
  const hasW3Results = useMemo(() => localFilteredResults.some(r => r.fundingType === 'W3Bilateral'), [localFilteredResults]);
  const hasFunderData = useMemo(() => funderMap.size > 0, [funderMap]);

  // Partner stats
  const partnerStats = useMemo(() => {
    const byDeliveryType = {};
    let total = 0;
    for (const p of partnerMap.values()) {
      total++;
      const types = p.deliveryTypes && p.deliveryTypes.length > 0 ? p.deliveryTypes : ['Other'];
      types.forEach(dt => {
        byDeliveryType[dt] = (byDeliveryType[dt] || 0) + 1;
      });
    }
    return { total, byDeliveryType };
  }, [partnerMap]);

  // Countries with partners (ISO3-deduplicated, matching map logic)
  const countriesWithPartners = useMemo(() => {
    const iso3Set = new Set();
    for (const r of localFilteredResults) {
      const preParsed = [...(r.partnersParsed || []), ...(r.partnersKPParsed || [])];
      let hasPartners = false;
      if (preParsed.length > 0) {
        hasPartners = preParsed.some(p => p.acronym);
      } else {
        const text = r.partners || r.partnersKP || '';
        hasPartners = text && text !== 'Not Applicable' && text !== 'Not applicable for KP';
      }
      if (!hasPartners) continue;
      const countries = r.countriesParsed || (r.countries ? parseCountryList(r.countries) : []);
      const globalSouthOnly = filters?.geographicFocus === 'globalSouth';
      for (const c of countries) {
        if (globalSouthOnly && !isGlobalSouthCountry(c)) continue;
        const iso3 = getISO3(c);
        if (iso3) iso3Set.add(iso3);
      }
    }
    return iso3Set.size;
  }, [localFilteredResults, filters?.geographicFocus]);

  // All unique delivery types
  const allDeliveryTypes = useMemo(() => {
    const types = new Set();
    fullPartnerMap.forEach(p => {
      (p.deliveryTypes || []).forEach(dt => types.add(dt));
    });
    return [...types].sort();
  }, [fullPartnerMap]);

  // W3 bilateral results
  const w3BilateralResults = useMemo(
    () => localFilteredResults.filter(r => r.fundingType === 'W3Bilateral'),
    [localFilteredResults]
  );

  const hasLocalFilters = deliveryTypeFilter !== 'all' || w3ProjectFilter !== 'all';
  const clearLocalFilters = () => {
    setDeliveryTypeFilter('all');
    setW3ProjectFilter('all');
  };

  const value = {
    // Raw from AppContext
    stats,
    filteredResults,
    reportType,
    isAccelerator,

    // Local filters
    deliveryTypeFilter,
    setDeliveryTypeFilter,
    w3ProjectFilter,
    setW3ProjectFilter,
    w3ProjectOptions,
    hasLocalFilters,
    clearLocalFilters,

    // Computed data
    localFilteredResults,
    resultsWithPartners,
    fullPartnerMap,
    partnerMap,
    linkageData,
    centerMap,
    funderMap,
    crossProgramData,
    networkData,
    w3BilateralResults,
    partnerMapByFunding,
    centerMapByFunding,
    crossProgramDataByFunding,

    // Stats & flags
    partnerStats,
    countriesWithPartners,
    allDeliveryTypes,
    hasW3Results,
    hasFunderData,
  };

  return (
    <PartnershipDataContext.Provider value={value}>
      {children}
    </PartnershipDataContext.Provider>
  );
}

/**
 * usePartnershipData
 * Hook to read partnership data and filters from context.
 * Returns an empty-shell object when called outside a provider,
 * so charts that use the `dataProp ?? hookValue` pattern degrade gracefully.
 */
const EMPTY_MAP = new Map();
const EMPTY_CONTEXT = {
  stats: null, filteredResults: [], reportType: 'program', isAccelerator: false,
  deliveryTypeFilter: 'all', setDeliveryTypeFilter: () => {},
  w3ProjectFilter: 'all', setW3ProjectFilter: () => {},
  w3ProjectOptions: [], hasLocalFilters: false, clearLocalFilters: () => {},
  localFilteredResults: [], resultsWithPartners: [],
  fullPartnerMap: EMPTY_MAP, partnerMap: EMPTY_MAP,
  linkageData: { totalPrograms: 0, jointResults: 0, programBreakdown: [] },
  centerMap: EMPTY_MAP, funderMap: EMPTY_MAP,
  crossProgramData: { matrix: [], programs: [] },
  networkData: { edges: [], programNodes: [], partnerNodes: [] },
  partnerMapByFunding: EMPTY_MAP,
  centerMapByFunding: EMPTY_MAP,
  crossProgramDataByFunding: { pairs: [], programs: [] },
  partnerStats: { total: 0, byDeliveryType: {} },
  countriesWithPartners: 0,
  allDeliveryTypes: [], hasW3Results: false, hasFunderData: false,
  w3BilateralResults: [],
};

export function usePartnershipData() {
  const context = useContext(PartnershipDataContext);
  return context || EMPTY_CONTEXT;
}
